/**
 * Per-language document metadata for the public landing page (Phase 56, committee R-1 / R-14).
 * The defaults live in public/index.html; this refreshes title, description, Open Graph tags and the JSON-LD graph
 * (Organization + FAQPage) whenever the landing language changes, and restores the previous values on unmount.
 */
const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

function setMeta(selector, attr, value) {
  const el = document.head.querySelector(selector);
  if (!el) return () => {};
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return () => { if (prev !== null) el.setAttribute(attr, prev); };
}

function jsonLd({ lang, description, faq }) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: "Tamreer",
        alternateName: "تمرير",
        url: ORIGIN ? `${ORIGIN}/` : "/",
        logo: ORIGIN ? `${ORIGIN}/apple-touch-icon.png` : "/apple-touch-icon.png",
        description,
        areaServed: "SA",
        address: { "@type": "PostalAddress", addressLocality: "Riyadh", addressCountry: "SA" },
      },
      {
        "@type": "FAQPage",
        inLanguage: lang,
        mainEntity: (faq || []).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
    ],
  };
}

/** Apply landing metadata; returns a cleanup that restores what was there before. */
export function applyLandingSeo({ lang, title, description, faq }) {
  if (typeof document === "undefined") return () => {};
  const prevTitle = document.title;
  document.title = title;
  const path = lang === "ar" ? "/ar" : "/";
  const undo = [
    setMeta('meta[name="description"]', "content", description),
    setMeta('meta[property="og:title"]', "content", title),
    setMeta('meta[property="og:description"]', "content", description),
    setMeta('meta[property="og:locale"]', "content", lang === "ar" ? "ar_SA" : "en_SA"),
    setMeta('meta[property="og:locale:alternate"]', "content", lang === "ar" ? "en_SA" : "ar_SA"),
    setMeta('meta[name="twitter:title"]', "content", title),
    setMeta('meta[name="twitter:description"]', "content", description),
    // Crawlers need absolute URLs for the share image and the page address; index.html can only carry relative defaults.
    ...(ORIGIN ? [
      setMeta('meta[property="og:image"]', "content", `${ORIGIN}/og-image.jpg`),
      setMeta('meta[name="twitter:image"]', "content", `${ORIGIN}/og-image.jpg`),
      setMeta('meta[property="og:url"]', "content", `${ORIGIN}${path}`),
      setMeta('link[rel="canonical"]', "href", `${ORIGIN}${path}`),
      setMeta('link[rel="alternate"][hreflang="en"]', "href", `${ORIGIN}/`),
      setMeta('link[rel="alternate"][hreflang="ar"]', "href", `${ORIGIN}/ar`),
      setMeta('link[rel="alternate"][hreflang="x-default"]', "href", `${ORIGIN}/`),
    ] : []),
  ];
  let script = document.getElementById("landing-jsonld");
  if (!script) {
    script = document.createElement("script");
    script.id = "landing-jsonld";
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(jsonLd({ lang, description, faq }));
  return () => {
    document.title = prevTitle;
    undo.forEach((fn) => fn());
    document.getElementById("landing-jsonld")?.remove();
  };
}


/** Lighter variant for other public pages (/pricing, /model): title, description, social title/description, canonical. */
export function applyPageSeo({ lang, title, description, path }) {
  if (typeof document === "undefined") return () => {};
  const prevTitle = document.title;
  document.title = title;
  const undo = [
    setMeta('meta[name="description"]', "content", description),
    setMeta('meta[property="og:title"]', "content", title),
    setMeta('meta[property="og:description"]', "content", description),
    setMeta('meta[property="og:locale"]', "content", lang === "ar" ? "ar_SA" : "en_SA"),
    setMeta('meta[name="twitter:title"]', "content", title),
    setMeta('meta[name="twitter:description"]', "content", description),
    ...(ORIGIN ? [setMeta('meta[property="og:url"]', "content", `${ORIGIN}${path}`), setMeta('link[rel="canonical"]', "href", `${ORIGIN}${path}`)] : []),
  ];
  return () => { document.title = prevTitle; undo.forEach((fn) => fn()); };
}
